import { useMemo } from 'react';
import { create } from 'zustand';

import { useCurrentUserOrNull } from '@/shared/auth/hooks';

import { useSearchRange, type PreviewOptions } from './api';
import type { PeriodPreset } from './period';

/** The palette's periods: any time, 24 hours, 7 days, 30 days. */
export const PALETTE_PERIODS = ['day', 'week', 'month'] as const satisfies readonly PeriodPreset[];

interface PaletteFilterState {
  /** Only this section's hits (a backend module id); null = all. */
  module: string | null;
  /** Only what changed within this preset; null = any time. */
  period: PeriodPreset | null;
  /** Only what the signed-in user wrote. */
  mine: boolean;
  setModule: (module: string | null) => void;
  setPeriod: (period: PeriodPreset | null) => void;
  setMine: (mine: boolean) => void;
  reset: () => void;
}

/** The ⌘K palette's filter row. Lives for as long as the palette is open: the palette resets it when it closes. */
export const usePaletteFilters = create<PaletteFilterState>()((set) => ({
  module: null,
  period: null,
  mine: false,
  setModule: (module) => set({ module }),
  setPeriod: (period) => set({ period }),
  setMine: (mine) => set({ mine }),
  reset: () => set({ module: null, period: null, mine: false }),
}));

/** What the palette's live search asks the server for under the chosen filters. */
export function usePaletteSearchOptions(): PreviewOptions {
  const module = usePaletteFilters((s) => s.module);
  const period = usePaletteFilters((s) => s.period);
  const mine = usePaletteFilters((s) => s.mine);
  const me = useCurrentUserOrNull();
  const range = useSearchRange({ period, from: null, to: null });
  const userId = me?.id ?? null;
  return useMemo(
    () => ({ module, author: mine ? userId : null, from: range.from, to: range.to }),
    [module, mine, userId, range.from, range.to],
  );
}
